// src/pages/Home.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import CategoryCard from '../components/CategoryCard';
import ProductCardSimple from '../components/ProductCardSimple';
import SearchBar from '../components/SearchBar';
import './Home.css';
import { useLocation } from 'react-router-dom';
import AdPopup from '../components/AdPopup';
import { Helmet, HelmetProvider } from 'react-helmet-async';

const apiUrl = process.env.REACT_APP_API_URL || 'https://www.nualily.es';

const Home = () => {
    const [categories, setCategories] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [initialProducts, setInitialProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [noResults, setNoResults] = useState(false);
    const [adData, setAdData] = useState(null);
    const [showAdPopup, setShowAdPopup] = useState(false);

    const location = useLocation();

    useEffect(() => {
        const fetchCategoriesAndProducts = async () => {
            try {
                const categoryResponse = await axios.get(`${apiUrl}/api/categories`);
                setCategories(categoryResponse.data);

                const productResponse = await axios.get(`${apiUrl}/api/products`);
                if (Array.isArray(productResponse.data)) {
                    setAllProducts(productResponse.data);
                    const shuffledProducts = [...productResponse.data].sort(() => 0.5 - Math.random());
                    const selectedProducts = shuffledProducts.slice(0, 20);
                    setInitialProducts(selectedProducts);
                    setFilteredProducts(selectedProducts);
                } else {
                    throw new Error('La respuesta de productos no es un arreglo');
                }
            } catch (error) {
                console.error('Error fetching categories and products:', error);
                setError('Error al cargar las categorías y productos. Intenta de nuevo más tarde.');
            } finally {
                setLoading(false);
            }
        };

         const fetchAdData = async () => {
            try {
                const response = await axios.get(`${apiUrl}/api/ad`);
                let ad = response.data;
                
              //  console.log('Respuesta completa del servidor:', ad);
        
                if (Array.isArray(ad) && ad.length > 0) {
                    ad = ad[0];
                } else if (ad.message) {
             //       console.log('Mensaje de error recibido del servidor:', ad.message);
                    setAdData(null);
                    setShowAdPopup(false);
                    return;
                }
        
                const now = new Date();
                const startDate = new Date(ad.startDate);
                const endDate = new Date(ad.endDate);
        
           //     console.log('Fechas - Ahora:', now, 'Inicio:', startDate, 'Fin:', endDate);
        
                if (now >= startDate && now <= endDate) {
                    setAdData(ad);
                    const lastAdViewTime = localStorage.getItem('lastAdViewTime');
                    const timeDiff = lastAdViewTime
                        ? Math.ceil((now - new Date(lastAdViewTime)) / (1000 * 60))
                        : ad.repeatInterval + 1;
        
                //    console.log('Tiempo desde la última visualización (redondeado):', timeDiff, 'Intervalo de repetición:', ad.repeatInterval);
        
                    if (timeDiff >= ad.repeatInterval) {
                        setShowAdPopup(true);
                        localStorage.setItem('lastAdViewTime', now.toISOString());
                   //     console.log('Anuncio mostrado, actualizando localStorage');
                    } else {
                 //       console.log('Anuncio no se muestra debido al intervalo de repetición.');
                    }
                } else {
               //     console.log('El anuncio no está dentro del intervalo de fechas de visualización.');
                }
            } catch (error) {
              //  console.error('Error al obtener el anuncio o no hay anuncio disponible:', error);
            }
        };
        
        
        
        fetchCategoriesAndProducts();
        fetchAdData();
    }, []);

    const handleSearch = (query) => {
        if (query.trim() === '') {
            setFilteredProducts(allProducts);
            setNoResults(false);
        } else {
            const filtered = allProducts.filter(product =>
                product.name.toLowerCase().includes(query.toLowerCase()) ||
                product.description.toLowerCase().includes(query.toLowerCase())
            );
            setFilteredProducts(filtered);
            setNoResults(filtered.length === 0);
        }
    };

    const handleCancel = () => {
        setSearchQuery('');
        setFilteredProducts(allProducts);
        setNoResults(false);
    };

    useEffect(() => {
        if (location.state?.reset) {
            console.log('Resetting search to show all products');
            setSearchQuery('');
            setFilteredProducts(allProducts);
            setNoResults(false);
            window.history.replaceState({}, document.title);
        }
    }, [location.state, allProducts]);

    if (loading) {
        return <p className="loading-message">Cargando categorías y productos...</p>;
    }

    if (error) {
        return <p className="error-message">{error}</p>;
    }

    const recommendedCategory = {
        _id: 'recommended',
        name: 'Recomendados',
        slug: 'recomendados',
    };

    // Obtener el estado de autenticación directamente desde localStorage
    const isAuthenticated = !!localStorage.getItem('token');
    console.log("Estado de autenticación en Home.js:", isAuthenticated);

    return (
        <HelmetProvider>
      <Helmet>
        <title>NUA Lily · Velas artesanales en Maresme</title>
        <meta
          name="description"
          content="Velas de soja hechas a mano en Maresme. Compra online tus velas aromáticas sostenibles y personalizadas."
        />
        <link rel="canonical" href="https://www.nualily.es/" />

        {/* Open Graph / WhatsApp */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="NUA Lily · Velas artesanales" />
        <meta property="og:description" content="Velas de soja hechas a mano en Maresme." />
        <meta property="og:url" content="https://www.nualily.es/" />
        <meta property="og:image" content="https://www.nualily.es/images/og-cover.jpg" />

        {/* Datos estructurados */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "NUA Lily",
            "url": "https://www.nualily.es",
            "logo": "https://www.nualily.es/logo512.png",
            "sameAs": ["https://www.instagram.com/nualily"]
          }
        `}</script>
      </Helmet>
        <div className="home-container main-content">
           {/* Muestra el popup de anuncio si está dentro de las fechas e intervalo */}
           {adData && showAdPopup && (
                <AdPopup 
                    adData={adData} 
                    setShowAdPopup={setShowAdPopup} 
                    isAuthenticated={isAuthenticated} 
                />
            )}
            <h1 className="home-title marca">Velas Artesanales Maresme </h1>
            <h2 className="home-subtitle">@nualily </h2>
            <SearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onSearch={handleSearch}
                products={allProducts}
                noResults={noResults}
                onCancel={handleCancel}
            />
            <div className="category-carousel">
                <div className="category-list">
                    <CategoryCard key={recommendedCategory._id} category={recommendedCategory} isRecommended />
                    {categories.length > 0 ? (
                        categories.map(category => (
                            <CategoryCard key={category._id} category={category} />
                        ))
                    ) : (
                        <p className="no-categories-message">No hay categorías disponibles.</p>
                    )}
                </div>
            </div>
            <h2 className="home-subtitle">Productos Recomendados</h2>
            <div className="product-grid">
                {filteredProducts.length > 0 ? (
                    filteredProducts.map(product => (
                        <ProductCardSimple key={product._id} product={product} />
                    ))
                ) : (
                    <p className="no-products-message">No hay productos que coincidan con tu búsqueda.</p>
                )}
            </div>
        </div>
        </HelmetProvider>
    );
};

export default Home;
